import React, { useState, useMemo } from 'react';
import { THEME } from '../../styles/theme';
import {
  calculateFibLevels, getOTEZone, getPricePosition,
  isInOTE, calculateRR, getMacroBuyZones, getMacroSellZones,
} from '../../utils/fibCalculator';
import { getCurrentPrice } from '../../services/storage';

// ── Styles ─────────────────────────────────────────────────────────────
const S = {
  page:  { padding: '20px', maxWidth: '100%', paddingBottom: '40px' },
  title: { fontSize: '22px', fontWeight: '700', color: THEME.colors.blue, letterSpacing: '2px', fontFamily: THEME.fonts.hud, marginBottom: '4px' },
  sub:   { fontSize: '13px', color: THEME.colors.textMuted, marginBottom: '20px' },
  row:   { display: 'flex', gap: '12px', flexWrap: 'wrap', marginBottom: '14px' },
  card:  (border) => ({
    background: 'rgba(255,255,255,0.03)',
    border: `1px solid ${border || 'rgba(255,255,255,0.07)'}`,
    borderRadius: '12px', padding: '16px', marginBottom: '16px',
  }),
  label: { fontSize: '11px', color: THEME.colors.textMuted, fontWeight: '700', letterSpacing: '1px', textTransform: 'uppercase', marginBottom: '6px' },
  input: {
    background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.1)',
    borderRadius: '8px', color: THEME.colors.text, fontSize: '14px',
    padding: '10px 12px', outline: 'none', width: '100%', boxSizing: 'border-box',
    fontFamily: THEME.fonts.hud,
  },
  btn: (v) => ({
    padding: '10px 20px', borderRadius: '8px', border: 'none', cursor: 'pointer',
    fontSize: '13px', fontWeight: '700', fontFamily: THEME.fonts.hud,
    background: v === 'primary' ? THEME.colors.blue
              : v === 'green'   ? THEME.colors.green
              : v === 'red'     ? THEME.colors.red
              : v === 'gold'    ? THEME.colors.gold
              : 'rgba(255,255,255,0.07)',
    color: ['primary','green','red','gold'].includes(v) ? '#000' : THEME.colors.textMuted,
  }),
  badge: (color, bg) => ({
    padding: '3px 10px', borderRadius: '20px', fontSize: '11px',
    fontWeight: '700', color, background: bg || 'transparent',
    display: 'inline-block', fontFamily: THEME.fonts.hud,
  }),
  sectionTitle: {
    fontSize: '10px', fontWeight: '700', letterSpacing: '2px',
    color: THEME.colors.textMuted, fontFamily: THEME.fonts.hud,
    marginBottom: '10px', textTransform: 'uppercase',
  },
};

function Inp({ label, ...props }) {
  return (
    <div style={{ flex: 1, minWidth: '140px' }}>
      {label && <div style={S.label}>{label}</div>}
      <input style={S.input} {...props} />
    </div>
  );
}

// ── Fib Level Row ───────────────────────────────────────────────────────
function LevelRow({ level, currentPrice }) {
  const isBuy  = level.zone === 'BUY';
  const isSell = level.zone === 'SELL';
  const isOTE  = level.zone === 'OTE';
  const nearPrice = currentPrice && Math.abs(currentPrice - level.price) <= 8;

  const bg = isBuy   ? 'rgba(0,255,136,0.07)'
           : isSell  ? 'rgba(255,51,51,0.07)'
           : isOTE   ? 'rgba(0,255,136,0.05)'
           : nearPrice ? 'rgba(0,212,255,0.06)'
           : 'transparent';

  const border = isBuy  ? '1px solid rgba(0,255,136,0.2)'
               : isSell ? '1px solid rgba(255,51,51,0.2)'
               : isOTE  ? '1px solid rgba(0,255,136,0.12)'
               : '1px solid transparent';

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', borderRadius: '8px', background: bg, border, marginBottom: '3px' }}>
      {nearPrice && <span style={{ fontSize: '13px' }}>◀</span>}
      <span style={{ flex: '0 0 130px', fontSize: '12px', fontFamily: THEME.fonts.hud, color: level.color, fontWeight: '700' }}>
        {level.label}
      </span>
      <span style={{ flex: '0 0 90px', fontSize: '15px', fontWeight: '800', color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>
        {level.price.toFixed(2)}
      </span>
      {isBuy  && <span style={S.badge('#000', THEME.colors.green)}>🟢 जुनं BUY ref</span>}
      {isSell && <span style={S.badge('#000', THEME.colors.red)}>🔴 जुनं SELL ref</span>}
      {isOTE  && !isBuy && <span style={S.badge(THEME.colors.green, 'rgba(0,255,136,0.1)')}>ICT OTE</span>}
      {level.zone === 'ext' && <span style={S.badge(THEME.colors.blue, 'rgba(0,212,255,0.1)')}>EXT TARGET</span>}
      {level.zone === 'mid' && <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>EQ</span>}
      {nearPrice && <span style={S.badge(THEME.colors.blue, 'rgba(0,212,255,0.15)')}>◀ PRICE</span>}
    </div>
  );
}

// ── Macro BUY/SELL Zone Summary ─────────────────────────────────────────
function MacroZoneCard({ levels, currentPrice }) {
  const buyZones  = getMacroBuyZones(levels);
  const sellZones = getMacroSellZones(levels);
  if (!buyZones.length && !sellZones.length) return null;

  const p = parseFloat(currentPrice) || 0;
  const nearBuy  = p > 0 && buyZones.some(z => Math.abs(p - z.price) <= 80);
  const nearSell = p > 0 && sellZones.some(z => Math.abs(p - z.price) <= 80);

  return (
    <div style={S.card(nearBuy ? 'rgba(0,255,136,0.4)' : nearSell ? 'rgba(255,51,51,0.3)' : 'rgba(255,215,0,0.15)')}>
      <div style={S.sectionTitle}>⚠️ जुने levels — कधीच backtest केलेले नाहीत, फक्त reference</div>
      <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
        {buyZones.map(z => (
          <div key={z.ratio} style={{ background: 'rgba(0,255,136,0.08)', border: '1px solid rgba(0,255,136,0.3)', borderRadius: '10px', padding: '10px 16px', minWidth: '140px' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.green, fontWeight: '700', letterSpacing: '1px', marginBottom: '4px' }}>🟢 BUY ZONE {z.ratio}</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: THEME.colors.green, fontFamily: THEME.fonts.hud }}>{z.price.toFixed(2)}</div>
            {p > 0 && p > z.price && <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '4px' }}>Price is {(p - z.price).toFixed(0)} above</div>}
            {p > 0 && p < z.price && <div style={{ fontSize: '11px', color: THEME.colors.green, marginTop: '4px' }}>↑ Price below — watch for bounce</div>}
          </div>
        ))}
        {sellZones.map(z => (
          <div key={z.ratio} style={{ background: 'rgba(255,51,51,0.08)', border: '1px solid rgba(255,51,51,0.3)', borderRadius: '10px', padding: '10px 16px', minWidth: '140px' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.red, fontWeight: '700', letterSpacing: '1px', marginBottom: '4px' }}>🔴 SELL ZONE {z.ratio}</div>
            <div style={{ fontSize: '22px', fontWeight: '900', color: THEME.colors.red, fontFamily: THEME.fonts.hud }}>{z.price.toFixed(2)}</div>
            {p > 0 && p < z.price && <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginTop: '4px' }}>Price is {(z.price - p).toFixed(0)} below</div>}
            {p > 0 && p > z.price && <div style={{ fontSize: '11px', color: THEME.colors.red, marginTop: '4px' }}>↓ Price above — watch for reversal</div>}
          </div>
        ))}
      </div>
      {nearBuy  && <div style={{ marginTop: '12px', fontSize: '13px', color: THEME.colors.green, fontWeight: '700' }}>Price जुन्या BUY zone जवळ आहे — पण हे backtest-validated नाही, फक्त reference म्हणून बघा</div>}
      {nearSell && <div style={{ marginTop: '12px', fontSize: '13px', color: THEME.colors.red,   fontWeight: '700' }}>Price जुन्या SELL zone जवळ आहे — पण हे backtest-validated नाही, फक्त reference म्हणून बघा</div>}
    </div>
  );
}

// ── ICT OTE Zone Card ────────────────────────────────────────────────────
function OTECard({ ote, currentPrice, trend }) {
  if (!ote) return null;
  const inOTE = currentPrice && currentPrice >= ote.bottom && currentPrice <= ote.top;
  return (
    <div style={S.card(inOTE ? 'rgba(0,255,136,0.4)' : 'rgba(0,255,136,0.15)')}>
      <div style={S.sectionTitle}>ICT OTE ZONE (0.618 — 0.786)</div>
      <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
        <div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '2px' }}>Bottom (0.618)</div>
          <div style={{ fontSize: '20px', fontWeight: '800', color: THEME.colors.green, fontFamily: THEME.fonts.hud }}>{ote.bottom}</div>
        </div>
        <div style={{ color: THEME.colors.textMuted, fontSize: '20px' }}>—</div>
        <div>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '2px' }}>Top (0.786)</div>
          <div style={{ fontSize: '20px', fontWeight: '800', color: THEME.colors.green, fontFamily: THEME.fonts.hud }}>{ote.top}</div>
        </div>
        <div style={{ marginLeft: '8px' }}>
          <div style={{ fontSize: '12px', color: THEME.colors.textMuted, marginBottom: '2px' }}>Mid (0.705)</div>
          <div style={{ fontSize: '18px', fontWeight: '700', color: THEME.colors.gold, fontFamily: THEME.fonts.hud }}>{ote.mid}</div>
        </div>
        {inOTE && (
          <span style={{ ...S.badge('#000', THEME.colors.green), fontSize: '13px', padding: '6px 16px' }}>
            ✅ IN OTE — {trend === 'up' ? 'LONG' : 'SHORT'} SETUP
          </span>
        )}
        {currentPrice && !inOTE && (
          <span style={S.badge(THEME.colors.textMuted, 'rgba(255,255,255,0.05)')}>
            Price {currentPrice > ote.top ? 'above' : 'below'} OTE zone
          </span>
        )}
      </div>
    </div>
  );
}

// ── R:R Calculator ───────────────────────────────────────────────────────
function RRCalculator() {
  const [f, setF] = useState({ entry: '', sl: '', tp1: '', tp2: '' });
  const set = (k, v) => setF(p => ({ ...p, [k]: v }));

  const rr1 = useMemo(() => calculateRR(f.entry, f.sl, f.tp1), [f.entry, f.sl, f.tp1]);
  const rr2 = useMemo(() => calculateRR(f.entry, f.sl, f.tp2), [f.entry, f.sl, f.tp2]);

  const rrColor = (ratio) => {
    const r = parseFloat(ratio);
    if (r >= 3) return THEME.colors.green;
    if (r >= 2) return THEME.colors.blue;
    if (r >= 1.5) return THEME.colors.gold;
    return THEME.colors.red;
  };

  return (
    <div style={S.card('rgba(0,212,255,0.15)')}>
      <div style={S.sectionTitle}>R:R CALCULATOR</div>
      <div style={S.row}>
        <Inp label="Entry Price"   type="number" step="0.01" placeholder="4030.00" value={f.entry} onChange={e => set('entry', e.target.value)} />
        <Inp label="Stop Loss"     type="number" step="0.01" placeholder="4010.00" value={f.sl}    onChange={e => set('sl',    e.target.value)} />
        <Inp label="Take Profit 1" type="number" step="0.01" placeholder="4080.00" value={f.tp1}   onChange={e => set('tp1',   e.target.value)} />
        <Inp label="Take Profit 2" type="number" step="0.01" placeholder="4120.00" value={f.tp2}   onChange={e => set('tp2',   e.target.value)} />
      </div>
      {rr1 && (
        <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap', marginTop: '6px' }}>
          <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '12px 18px', minWidth: '160px' }}>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>TP1 RESULT</div>
            <div style={{ fontSize: '26px', fontWeight: '900', color: rrColor(rr1.ratio), fontFamily: THEME.fonts.hud }}>{rr1.text}</div>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Risk {rr1.risk} | Reward {rr1.reward}</div>
            <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>SL = {rr1.pips} pips</div>
          </div>
          {rr2 && (
            <div style={{ background: 'rgba(0,0,0,0.3)', borderRadius: '10px', padding: '12px 18px', minWidth: '160px' }}>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted, marginBottom: '4px' }}>TP2 RESULT</div>
              <div style={{ fontSize: '26px', fontWeight: '900', color: rrColor(rr2.ratio), fontFamily: THEME.fonts.hud }}>{rr2.text}</div>
              <div style={{ fontSize: '11px', color: THEME.colors.textMuted }}>Risk {rr2.risk} | Reward {rr2.reward}</div>
            </div>
          )}
          <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: '6px' }}>
            {parseFloat(rr1.ratio) >= 2    && <span style={S.badge('#000', THEME.colors.green)}>✅ Good R:R</span>}
            {parseFloat(rr1.ratio) < 1.5   && <span style={S.badge(THEME.colors.red,  'rgba(255,51,51,0.1)')}>⚠️ R:R too low</span>}
            {parseFloat(rr1.ratio) >= 1.5 && parseFloat(rr1.ratio) < 2 && <span style={S.badge(THEME.colors.gold, 'rgba(255,215,0,0.1)')}>⚡ Acceptable</span>}
          </div>
        </div>
      )}
    </div>
  );
}

// ── Main Component ──────────────────────────────────────────────────────
export default function FibCalculator() {
  const livePrice = getCurrentPrice();
  // Default is 'ict' (validated 0.618-0.79 OTE — matches A_Signal.js /
  // JarvisSMC_Strategy.pine exactly). 'macro' (Sir's old 0.15/0.75/0.84/0.90
  // levels) was the default before — those were NEVER backtested and this
  // screen called them "PROVEN"/"HIGH PROBABILITY", which was false. Kept
  // available as a manual reference tool, relabeled honestly, no longer
  // the default view.
  const [mode, setMode] = useState('ict'); // 'ict' | 'macro'
  const [form, setForm] = useState({ high: '', low: '', trend: 'up', customPrice: '' });
  const [levels, setLevels] = useState(null);
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleCalc = () => {
    const result = calculateFibLevels(form.high, form.low, form.trend, mode);
    setLevels(result);
  };

  const handleModeSwitch = (m) => {
    setMode(m);
    if (form.high && form.low) {
      setLevels(calculateFibLevels(form.high, form.low, form.trend, m));
    }
  };

  const handleReset = () => { setLevels(null); setForm({ high: '', low: '', trend: 'up', customPrice: '' }); };

  const usePrice = livePrice > 0 ? livePrice : parseFloat(form.customPrice) || null;
  const ote      = levels && mode === 'ict' ? getOTEZone(levels, form.trend) : null;
  const pricePos = levels && usePrice ? getPricePosition(usePrice, levels) : null;
  const inOTE    = levels && usePrice && mode === 'ict' ? isInOTE(usePrice, levels, form.trend) : false;

  return (
    <div style={S.page}>
      <div style={S.title}>🔢 FIBONACCI CALCULATOR</div>
      <div style={S.sub}>Validated ICT OTE (0.618-0.79) — Structure+ADX सोबत हेच वापरतो. जुने levels फक्त reference tab मध्ये आहेत.</div>

      {/* Mode Toggle */}
      <div style={{ display: 'flex', gap: '10px', marginBottom: '16px' }}>
        <button style={{ ...S.btn(mode === 'macro' ? 'green' : ''), flex: 1 }} onClick={() => handleModeSwitch('macro')}>
          🗂️ जुने Levels (0.15/0.75/0.84/0.90) — reference only
        </button>
        <button style={{ ...S.btn(mode === 'ict' ? 'primary' : ''), flex: 1 }} onClick={() => handleModeSwitch('ict')}>
          📐 ICT Standard (OTE 0.618–0.786)
        </button>
      </div>

      {/* Mode Description */}
      <div style={{ ...S.card(mode === 'macro' ? 'rgba(0,255,136,0.15)' : 'rgba(0,212,255,0.15)'), padding: '10px 14px', marginBottom: '14px' }}>
        {mode === 'macro' ? (
          <div style={{ fontSize: '13px', color: THEME.colors.textMuted }}>
            <strong style={{ color: THEME.colors.green }}>जुने Levels Mode:</strong> हे कधीच backtest केलेले नाहीत —
            <span style={{ color: THEME.colors.green }}> 0.15 आणि 0.75</span>,
            <span style={{ color: THEME.colors.red }}> 0.84 आणि 0.90</span> — फक्त ऐतिहासिक संदर्भासाठी, trade decision साठी वापरू नका.
          </div>
        ) : (
          <div style={{ fontSize: '13px', color: THEME.colors.textMuted }}>
            <strong style={{ color: THEME.colors.blue }}>ICT OTE Mode:</strong> Standard ICT Optimal Trade Entry —
            <span style={{ color: THEME.colors.green }}> 0.618–0.786 = intraday entry zone</span>.
            Best for London/NY session entries on H1/H4.
          </div>
        )}
      </div>

      {/* Input Form */}
      <div style={S.card('rgba(0,212,255,0.2)')}>
        <div style={S.sectionTitle}>SWING POINTS</div>
        <div style={S.row}>
          <Inp label="Swing High" type="number" step="0.01" placeholder="e.g. 5866.16" value={form.high} onChange={e => set('high', e.target.value)} />
          <Inp label="Swing Low"  type="number" step="0.01" placeholder="e.g. 3113.22" value={form.low}  onChange={e => set('low',  e.target.value)} />
          <div style={{ flex: '0 0 150px' }}>
            <div style={S.label}>Trend</div>
            <div style={{ display: 'flex', gap: '8px' }}>
              {['up','down'].map(t => (
                <button key={t} style={{ ...S.btn(form.trend === t ? 'primary' : ''), flex: 1 }} onClick={() => set('trend', t)}>
                  {t === 'up' ? '▲ UP' : '▼ DOWN'}
                </button>
              ))}
            </div>
          </div>
        </div>
        {livePrice <= 0 && (
          <div style={{ marginBottom: '10px' }}>
            <Inp label="Current Price (manual)" type="number" step="0.01" placeholder="4027.99" value={form.customPrice} onChange={e => set('customPrice', e.target.value)} />
          </div>
        )}
        {livePrice > 0 && (
          <div style={{ fontSize: '13px', color: THEME.colors.green, marginBottom: '10px' }}>
            Live XAU/USD: <strong>{livePrice}</strong> — auto-used for zone detection
          </div>
        )}
        <div style={{ display: 'flex', gap: '10px' }}>
          <button style={S.btn('primary')} onClick={handleCalc}>Calculate</button>
          {levels && <button style={S.btn()} onClick={handleReset}>Reset</button>}
        </div>
      </div>

      {/* Macro BUY/SELL Zone Summary */}
      {levels && mode === 'macro' && <MacroZoneCard levels={levels} currentPrice={usePrice} />}

      {/* ICT OTE Zone */}
      {levels && mode === 'ict' && <OTECard ote={ote} currentPrice={usePrice} trend={form.trend} />}

      {/* Price position */}
      {pricePos && (
        <div style={{ ...S.card('rgba(0,212,255,0.12)'), padding: '10px 14px', marginBottom: '14px' }}>
          <span style={{ fontSize: '13px', color: THEME.colors.blue }}>
            Price <strong>{usePrice}</strong> is between{' '}
            <strong style={{ color: THEME.colors.gold }}>{pricePos.between[0].label}</strong> ({pricePos.between[0].price}) and{' '}
            <strong style={{ color: THEME.colors.gold }}>{pricePos.between[1].label}</strong> ({pricePos.between[1].price})
            — {pricePos.pct}% through zone
          </span>
          {inOTE && <span style={{ ...S.badge('#000', THEME.colors.green), marginLeft: '10px' }}>OTE CONFIRMED</span>}
        </div>
      )}

      {/* Level Table */}
      {levels && (() => {
        const sorted    = [...levels].sort((a, b) => b.price - a.price); // highest price first
        const extLevels = sorted.filter(l => l.ratio > 1);
        const retLevels = sorted.filter(l => l.ratio <= 1);
        return (
          <div style={S.card()}>
            <div style={S.sectionTitle}>
              {mode === 'macro' ? "SIR'S MACRO FIB LEVELS" : 'ICT FIBONACCI LEVELS'}{' '}
              {form.trend === 'up' ? '(Bullish)' : '(Bearish)'}
            </div>

            {/* Extension TP section — only in macro mode */}
            {mode === 'macro' && extLevels.length > 0 && (
              <>
                <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1px', color: THEME.colors.blue, padding: '6px 0 4px', borderBottom: '1px solid rgba(0,212,255,0.3)', marginBottom: '4px' }}>
                  EXTENSION TARGETS (TP LEVELS &gt;1)
                </div>
                {extLevels.map((level, i) => <LevelRow key={`ext-${i}`} level={level} currentPrice={usePrice} />)}
                <div style={{ margin: '8px 0', borderTop: '2px dashed rgba(255,255,255,0.15)', paddingTop: '8px' }} />
              </>
            )}

            {/* Retracement section */}
            {mode === 'macro' && (
              <div style={{ fontSize: '11px', fontWeight: '700', letterSpacing: '1px', color: THEME.colors.textMuted, padding: '2px 0 4px', borderBottom: '1px solid rgba(255,255,255,0.1)', marginBottom: '4px' }}>
                RETRACEMENT LEVELS (0–1)
              </div>
            )}
            {retLevels.map((level, i) => <LevelRow key={`ret-${i}`} level={level} currentPrice={usePrice} />)}

            <div style={{ marginTop: '12px', fontSize: '11px', color: THEME.colors.textMuted }}>
              {mode === 'macro'
                ? '⚠️ जुने ref: 0.15 आणि 0.75 (BUY) | 0.84 आणि 0.90 (SELL) — backtest केलेलं नाही'
                : '⭐ ICT OTE Zone (0.618-0.79) = Validated Optimal Trade Entry — A_Signal.js हेच वापरतो'}
            </div>
          </div>
        );
      })()}

      {/* R:R Calculator */}
      <RRCalculator />

      {/* Combined Reference Card */}
      <div style={{ ...S.card('rgba(255,215,0,0.1)'), padding: '14px 16px' }}>
        <div style={S.sectionTitle}>खरं validated strategy काय वापरते</div>
        <div style={{ fontSize: '13px', color: THEME.colors.textMuted, lineHeight: '1.8' }}>
          <div>✅ <strong style={{ color: THEME.colors.gold }}>Jarvis खरंच फक्त हेच वापरतो:</strong> Structure (50-bar swing) + OTE zone (0.618-0.79, हाच ICT टॅब) + ADX trending + HTF match + confirmation candle — हे सगळे एकाच वेळी जुळले तरच trade.</div>
          <div>⚠️ <strong style={{ color: THEME.colors.gold }}>वरचा 'जुने levels' टॅब (0.15/0.75/0.84/0.90):</strong> हा फक्त reference आहे, कधीच backtest केलेला नाही — trade decision यावर आधारित नाही.</div>
        </div>
      </div>
    </div>
  );
}

